/**
 * Portfolios → No cost (COST-FIX, Pedro 2026-10-27: "make it so that we can easily retrieve all the
 * securities in all portfolios for which there isn't cost information").
 *
 * Every security line whose cost the custodian does not give (Average purchase price and Total cost
 * 0, or blank — C-411), in each portfolio's latest positions, across all portfolios. Read-only for
 * Portfolios readers; a client-data read like View portfolio (rendered only while its tab is open).
 *
 * COST-SRC B (migration 0127, Pedro 2026-10-27: "Let's do B now and use the No Cost tab to input the
 * cost"): holders of `portfolios.cost` enter the Average purchase price and the Average buy exchange
 * rate, with the evidence. The entry is applied wherever the line is read, only while the custodian
 * gives no cost; shown cyan there. Each Save / Remove commits at once through this modal (§3g
 * Pattern B: the modal is the transaction) and is audited.
 */
import { useMemo, useState } from 'react';
import {
  Alert, Anchor, Badge, Button, Group, Loader, Modal, NumberInput, Stack, Table, Text, Textarea, TextInput, Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconSearch } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { portfoliosApi, type NoCostLine } from '../api/portfolios';
import { usePermissions } from '../hooks/usePermissions';
import { securityLink } from '../utils/securityLink';
import { DisabledTooltipButton } from './DisabledTooltipButton';

type SortKey = 'label' | 'agreement_label' | 'as_of' | 'name' | 'isin' | 'currency' | 'quantity' | 'value_ref';

const num = (s: string | number | null | undefined, d = 2, max = d) =>
  s == null || s === '' ? '-' : Number(s).toLocaleString('en-GB', { minimumFractionDigits: d, maximumFractionDigits: max });

export function PortfolioNoCostTab({ onOpenPortfolio }: { onOpenPortfolio: (ref: string) => void }) {
  const { can } = usePermissions();
  const canEnter = can('portfolios.cost');
  const q = useQuery({ queryKey: ['portfolios', 'no-cost'], queryFn: () => portfoliosApi.noCost() });
  const [term, setTerm] = useState('');
  const [sort, setSort] = useState<{ key: SortKey; desc: boolean }>({ key: 'label', desc: false });
  const [editing, setEditing] = useState<NoCostLine | null>(null);

  const rows = useMemo(() => {
    const t = term.trim().toLowerCase();
    const items = (q.data?.items ?? []).filter((r) => !t || [r.label, r.agreement_label, r.name, r.isin, r.currency]
      .some((v) => (v ?? '').toLowerCase().includes(t)));
    const val = (r: NoCostLine): string | number =>
      sort.key === 'quantity' || sort.key === 'value_ref' ? Number(r[sort.key] ?? 0) : (r[sort.key] ?? '');
    return [...items].sort((a, b) => {
      const x = val(a), y = val(b);
      const c = typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y));
      return sort.desc ? -c : c;
    });
  }, [q.data, term, sort]);

  if (q.isLoading) return <Loader size="sm" />;
  if (q.error) return <Alert color="red" title="The list could not be loaded">{(q.error as Error).message}</Alert>;

  const head = (key: SortKey, label: string, right = false, tip?: string) => (
    <Table.Th ta={right ? 'right' : undefined} style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
      onClick={() => setSort((s) => ({ key, desc: s.key === key ? !s.desc : false }))} title={tip ?? 'Click to sort'}>
      {label}{sort.key === key ? (sort.desc ? ' ↓' : ' ↑') : ''}
    </Table.Th>
  );
  const items = q.data?.items ?? [];
  const n = items.length;
  const entered = items.filter((i) => i.entry).length;
  const why = 'Entering a cost needs the portfolios.cost permission (Admin by default; given per person in Admin → Permissions).';
  return (
    <>
      <Group justify="space-between" mb="xs">
        <Text size="sm">
          {n === 0
            ? 'Every security line in the latest positions has a cost from the custodian.'
            : <><b>{n}</b> security line{n === 1 ? '' : 's'} without a cost from the custodian, in <b>{q.data?.portfolios}</b> portfolio{q.data?.portfolios === 1 ? '' : 's'} (each portfolio&apos;s latest positions){entered > 0 && <>; a cost has been entered in the app for <b>{entered}</b></>}.</>}
        </Text>
        <TextInput size="xs" w={280} placeholder="Search portfolio, agreement, security, ISIN" leftSection={<IconSearch size={14} />}
          value={term} onChange={(e) => setTerm(e.currentTarget.value)} />
      </Group>
      <Text size="xs" c="dimmed" mb="xs">
        The custodian gives no cost for these lines (Average purchase price and Total cost 0 or blank), so their cost and
        P&amp;L are not reliable: View portfolio shows them as given, with a yellow banner. A cost entered here (the average
        purchase price and the average buy exchange rate, with where they come from) is used wherever the line is shown,
        in cyan, until the custodian&apos;s own file carries a cost; the positions as the custodian sent them are never changed.
      </Text>
      {n > 0 && (
        <Table.ScrollContainer minWidth={1100}>
          <Table striped highlightOnHover withTableBorder fz="sm" verticalSpacing={4}>
            <Table.Thead>
              <Table.Tr>
                {head('label', 'Portfolio')}
                {head('agreement_label', 'Agreement')}
                {head('as_of', 'Positions date')}
                {head('name', 'Security')}
                {head('isin', 'ISIN')}
                {head('currency', 'Sec Ccy')}
                {head('quantity', 'Quantity', true)}
                {head('value_ref', 'Value (portfolio Ref Ccy)', true,
                  "In the portfolio's own reference currency: the file's Net value plus its Accrued interest. Click to sort")}
                <Table.Th>Cost entered in the app</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((r) => (
                <Table.Tr key={`${r.ref}|${r.isin ?? r.name}|${r.currency}`}>
                  <Table.Td>
                    <Tooltip withArrow label="Open this portfolio in View portfolio">
                      <Anchor size="sm" onClick={() => onOpenPortfolio(r.ref)}>{r.label}</Anchor>
                    </Tooltip>
                  </Table.Td>
                  <Table.Td>{r.agreement_id
                    ? <Anchor component={Link} size="sm" to={`/agreements/${r.agreement_id}`}>{r.agreement_label}</Anchor>
                    : <Text size="sm" c="dimmed">Not linked</Text>}</Table.Td>
                  <Table.Td>{r.as_of}</Table.Td>
                  <Table.Td>{r.security_id && r.isin
                    ? <Anchor component={Link} size="sm" to={securityLink(r.isin)}>{r.name}</Anchor> : r.name}</Table.Td>
                  <Table.Td>{r.isin ?? '-'}</Table.Td>
                  <Table.Td>{r.currency}</Table.Td>
                  <Table.Td ta="right">{num(r.quantity, 2)}</Table.Td>
                  <Table.Td ta="right">{num(r.value_ref)} {r.ref_currency ?? ''}</Table.Td>
                  <Table.Td style={r.entry ? { background: 'var(--mantine-color-cyan-light)' } : undefined}>
                    {r.entry ? (
                      <Tooltip withArrow multiline w={360}
                        label={`${r.entry.note}${r.entry.updated_by_name ? ` — ${r.entry.updated_by_name}` : ''}${r.entry.updated_at ? `, ${r.entry.updated_at.slice(0, 10)}` : ''}`}>
                        <Text size="sm">
                          APP {num(r.entry.avg_price, 2, 6)} {r.currency} · rate {num(r.entry.avg_buy_fx, 4, 10)} → Total cost <b>{num(r.total_cost)} {r.ref_currency ?? ''}</b>
                        </Text>
                      </Tooltip>
                    ) : <Badge color="yellow" variant="light">Not entered</Badge>}
                  </Table.Td>
                  <Table.Td>
                    {r.isin && (
                      <DisabledTooltipButton size="compact-xs" variant="light" disabledReason={canEnter ? undefined : why}
                        onClick={() => setEditing(r)}>
                        {r.entry ? 'Edit cost' : 'Enter cost'}
                      </DisabledTooltipButton>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
      {editing && <CostModal line={editing} onClose={() => setEditing(null)} />}
    </>
  );
}

function CostModal({ line, onClose }: { line: NoCostLine; onClose: () => void }) {
  const qc = useQueryClient();
  const sameCcy = line.currency === (line.ref_currency ?? 'EUR');
  const factor = Number(line.unit_factor ?? '1');
  const [app, setApp] = useState<string | number>(line.entry?.avg_price ?? '');
  const [fx, setFx] = useState<string | number>(sameCcy ? 1 : (line.entry?.avg_buy_fx ?? ''));
  const [note, setNote] = useState(line.entry?.note ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);

  const a = Number(app), f = Number(fx), qty = Number(line.quantity ?? 0);
  const cost = app !== '' && fx !== '' && a > 0 && f > 0 ? qty * a * factor * f : null;
  const reason = !(a > 0) ? 'Enter the average purchase price (more than 0).'
    : !(f > 0) ? 'Enter the average buy exchange rate (more than 0).'
    : !note.trim() ? 'Say where the cost comes from (the evidence).' : undefined;

  const done = (msg: string) => {
    void qc.invalidateQueries({ queryKey: ['portfolios'] });
    notifications.show({ color: 'green', message: msg });
    onClose();
  };
  const save = async () => {
    setBusy(true); setError(null);
    try {
      await portfoliosApi.saveCost({ portfolio: line.ref, isin: line.isin!, currency: line.currency,
        avg_price: String(app), avg_buy_fx: String(fx), note: note.trim() });
      done(`Cost saved for ${line.name} in ${line.label}.`);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!line.entry) return;
    setBusy(true); setError(null);
    try {
      await portfoliosApi.removeCost(line.entry.id);
      done(`Cost removed for ${line.name} in ${line.label}: the line has no cost again.`);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  return (
    <Modal opened onClose={onClose} centered size="lg" title={`${line.entry ? 'Edit' : 'Enter'} cost · ${line.name}`}>
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          {line.label} · {line.isin} {line.currency} · quantity {num(line.quantity, 2, 6)} · positions of {line.as_of}
        </Text>
        <NumberInput label={`Average purchase price (${line.currency})`} required decimalScale={10} min={0}
          description={factor === 0.01 ? 'Per 100 nominal, as the custodian gives a bond’s price.' : 'Per unit, in the security’s currency.'}
          value={app} onChange={setApp} />
        <NumberInput label={`Average buy exchange rate (${line.currency} → ${line.ref_currency ?? 'EUR'})`} required decimalScale={10} min={0}
          description={sameCcy ? 'The security is in the portfolio’s reference currency: the rate is 1.'
            : `${line.ref_currency ?? 'EUR'} per 1 ${line.currency} at purchase (the file’s “Average buy exchange rate”).`}
          value={fx} onChange={setFx} disabled={sameCcy} />
        <Textarea label="Where the cost comes from (evidence)" required autosize minRows={2}
          placeholder="e.g. previous custodian's statement of 2025-12-31; transfer letter ref. …"
          value={note} onChange={(e) => setNote(e.currentTarget.value)} />
        <Text size="sm">
          Total cost ({line.ref_currency ?? 'EUR'}) = quantity × price{factor === 0.01 ? ' ÷ 100' : ''} × rate ={' '}
          <b>{cost == null ? '-' : num(cost)}</b>. Total P&amp;L will be the Net value minus this cost.
        </Text>
        {error && <Alert color="red">{error}</Alert>}
        {confirmRemove ? (
          <Alert color="red" title="Remove this cost?">
            <Text size="sm">The line goes back to having no cost (shown as the custodian gives it, 0). The removal is kept in the Audit log.</Text>
            <Group justify="flex-end" mt="xs">
              <Button size="xs" variant="default" onClick={() => setConfirmRemove(false)}>Cancel</Button>
              <Button size="xs" color="red" loading={busy} onClick={() => void remove()}>Remove cost</Button>
            </Group>
          </Alert>
        ) : (
          <Group justify="space-between">
            {line.entry
              ? <Button size="xs" color="red" variant="subtle" onClick={() => setConfirmRemove(true)}>Remove cost</Button>
              : <span />}
            <Group gap="xs">
              <Button size="xs" variant="default" onClick={onClose}>Cancel</Button>
              <DisabledTooltipButton size="xs" loading={busy} disabledReason={reason} onClick={() => void save()}>Save cost</DisabledTooltipButton>
            </Group>
          </Group>
        )}
      </Stack>
    </Modal>
  );
}
